import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  schema?: object;
}

export const useSEO = ({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogType = 'website',
  schema
}: SEOProps) => {
  useEffect(() => {
    // Update title
    document.title = title;

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    updateMetaTag('og:title', ogTitle || title, true);
    updateMetaTag('og:description', ogDescription || description, true);
    updateMetaTag('og:type', ogType, true);

    // Update canonical
    if (canonical) {
      let linkElement = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!linkElement) {
        linkElement = document.createElement('link');
        linkElement.rel = 'canonical';
        document.head.appendChild(linkElement);
      }
      const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
      linkElement.href = `${siteUrl}${canonical}`;
    }

    // Add/Update Schema.org JSON-LD
    if (schema) {
      let scriptElement = document.querySelector('script[type="application/ld+json"]#page-schema');
      if (!scriptElement) {
        scriptElement = document.createElement('script');
        scriptElement.type = 'application/ld+json';
        scriptElement.id = 'page-schema';
        document.head.appendChild(scriptElement);
      }
      
      const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
      const schemaWithUrl = JSON.parse(JSON.stringify(schema).replace(/\{\{SITE_URL\}\}/g, siteUrl));
      scriptElement.textContent = JSON.stringify(schemaWithUrl);
    }

    // Cleanup function
    return () => {
      const schemaScript = document.querySelector('script[type="application/ld+json"]#page-schema');
      if (schemaScript) {
        schemaScript.remove();
      }
    };
  }, [title, description, keywords, canonical, ogTitle, ogDescription, ogType, schema]);
};
